import React, { useEffect, useState } from "react";
import { object, string } from "yup";
import { updateFundingApplication } from "../../../api/funding-application";
import {
  Button,
  FileUploadField,
  Form,
  SSNInputField,
} from "../../../components/common";
import { useCurrentFundingApplication } from "../../../hooks";
import "./Documents.scss";

const validationSchema = object({
  verifyIdentity: string().required("This is a required field"),
  uploadTranscript: string().required("This is a required field"),
  backOfDrivingLicense: string().required("This is a required field"),
  selfiePicture: string().required("This is a required field"),
  recentResume: string().required("This is a required field"),
  proofOfAddress: string().required("This is a required field"),
  // socialSecurityNumber: string()
  //   .required("This is a required field")
  //   .test("isSSNValid", "This is not a valid social security number", (v) => {
  //     if (!v) return false;
  //     return v.replaceAll("-", "").replaceAll(/\s/g, "").length === 9;
  //   }),
  // agreement: boolean().isTrue("You must agree to the terms above"),
});

const Documents = ({ onProceed, isReadonly = false }) => {
  const {currentApplication, fundingApplicationId} =
    useCurrentFundingApplication();
  const [readonlyData, setReadonlyData] = useState({});

  const handleSubmit = async (data) => {
    // if is readonly mode - just change view
    if (isReadonly) {
      onProceed("terminated");
      return;
    }

    const {
      verifyIdentity,
      uploadTranscript,
      socialSecurityNumber,
      backOfDrivingLicense,
      selfiePicture,
      recentResume,
      proofOfAddress,
    } = data;

    let submitData = {
      transition: "to_terminated",
      socialSecurityNumber,
      uploads: [
        verifyIdentity,
        uploadTranscript,
        backOfDrivingLicense,
        selfiePicture,
        recentResume,
        proofOfAddress,
      ],
    };

    try {
      await updateFundingApplication({
        data: submitData,
        id: fundingApplicationId,
      }).then(() => {
        onProceed("terminated");
        localStorage.setItem("old_dashboard", true);
      });
    } catch (e) {
      // console.log("Error: ", e);
    }

    if (window.dataLayer && typeof window.dataLayer.push === "function") {
      window.dataLayer.push({
        event: "submitted_finish",
        category: "Form Submission",
        action: "submit",
        label: "Completed Documents Form",
        value: 1,
      });
    }

    if (window.ttq && typeof window.ttq.track === "function") {
      window.ttq.track("submitted_finish");
    }
  };
  useEffect(() => {
    document.body.classList.add("documents");
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });

    return () => {
      document.body.classList.remove("documents");
    };
  }, []);

  useEffect(() => {
    if (!isReadonly) return;

    setReadonlyData({
      identity: {
        defaultValue: currentApplication?.uploads?.filter(
          (file) => file.type === "TYPE_ID"
        )[0],
        isReadonly: true,
      },
      transcript: {
        defaultValue: currentApplication?.uploads?.filter(
          (file) => file.type === "TYPE_TRANSCRIPT"
        )[0],
        isReadonly: true,
      },
      socialSecurityNumber: {
        defaultValue: currentApplication?.socialSecurityNumber,
        isReadonly: true,
      },
      back_driver: {
        defaultValue: currentApplication?.uploads?.filter(
          (file) => file.type === "TYPE_DLB"
        )[0],
        isReadonly: true,
      },
      selfie_driver: {
        defaultValue: currentApplication?.uploads?.filter(
          (file) => file.type === "TYPE_SDL"
        )[0],
        isReadonly: true,
      },
      cor: {
        defaultValue: currentApplication?.uploads?.filter(
          (file) => file.type === "TYPE_COR"
        )[0],
        isReadonly: true,
      },
      poa: {
        defaultValue: currentApplication?.uploads?.filter(
          (file) => file.type === "TYPE_POA"
        )[0],
        isReadonly: true,
      },
    });
  }, [isReadonly, currentApplication]);

  return (
    <div className="step-wrapper personal-info flex-column align-center step--documents">
      <h1>Last steps, promise! Upload documents.</h1>
      <Form onSubmit={handleSubmit} validationSchema={validationSchema}>
        <div className="flex-column gap-3rem">
          <SSNInputField
            id="socialSecurityNumber"
            name="socialSecurityNumber"
            placeholder="XXX-XX-XXXX"
            labelAbove="Social Security Number"
            {...readonlyData.socialSecurityNumber}
            // required
          />
          <FileUploadField
            id="verifyIdentity"
            name="verifyIdentity"
            title={"Front of ID"}
            uploadLabel={
              "Upload (driver license, passport or State issued ID)"
            }
            helperText={"Supported formats: .jpeg, .png, .pdf"}
            allowedTypes={[".jpeg", ".jpg", ".png", ".pdf"]}
            fileUploadType={"TYPE_ID"}
            {...readonlyData.identity}
            required
          />
          <FileUploadField
            id="backOfDrivingLicense"
            name="backOfDrivingLicense"
            labelAbove={"Back of ID"}
            helperText={"Supported formats: .jpeg, .png, .pdf"}
            allowedTypes={[".jpeg", ".jpg", ".png", ".pdf"]}
            title={"Back of ID"}
            uploadLabel={"Upload Back of ID"}
            fileUploadType={"TYPE_DLB"}
            {...readonlyData.back_driver}
            required
          />
          <FileUploadField
            id="uploadTranscript"
            name="uploadTranscript"
            labelAbove={"Transcript"}
            helperText={"Supported formats: .jpeg, .png, .pdf"}
            allowedTypes={[".jpeg", ".jpg", ".png", ".pdf"]}
            title={"Transcript"}
            uploadLabel={"Upload your most recent transcript"}
            fileUploadType={"TYPE_TRANSCRIPT"}
            {...readonlyData.identity}
            required
          />
          <FileUploadField
            id="selfiePicture"
            name="selfiePicture"
            labelAbove={"Selfie picture of you holding your driver's license"}
            helperText={"Supported formats: .jpeg, .png, .pdf"}
            allowedTypes={[".jpeg", ".jpg", ".png", ".pdf"]}
            title={"Selfie picture of you holding your driver's license"}
            uploadLabel={"Upload Selfie"}
            fileUploadType={"TYPE_SDL"}
            {...readonlyData.selfie_driver}
            required
          />
          <FileUploadField
            id="recentResume"
            name="recentResume"
            labelAbove={"Most recent resume"}
            helperText={"Supported formats: .jpeg, .png, .pdf"}
            allowedTypes={[".jpeg", ".jpg", ".png", ".pdf"]}
            title={"Most recent resume"}
            uploadLabel={"Upload Resume"}
            fileUploadType={"TYPE_COR"}
            {...readonlyData.cor}
            required
          />
          <FileUploadField
            id="proofOfAddress"
            name="proofOfAddress"
            labelAbove={"Proof of Address (utility bill or bank statement)"}
            helperText={"Supported formats: .jpeg, .png, .pdf"}
            allowedTypes={[".jpeg", ".jpg", ".png", ".pdf"]}
            title={"Proof of Address (utility bill or bank statement)"}
            uploadLabel={
              "Upload Proof of Address"
            }
            fileUploadType={"TYPE_POA"}
            {...readonlyData.poa}
            required
          />
          <Button
            className="primary full-width xl"
            type="submit"
            label="Finish application"
            onClick={() => {
              if (isReadonly) {
                handleSubmit();
                return false;
              }
              return true;
            }}
          />
        </div>
      </Form>
    </div>
  );
};

export default Documents;
